import { css } from 'styled-components';
import { mix, shade } from 'polished';

/** Cores oficiais da marca Fuzzy Makers. */
export const brand = {
  green: '#4CAF50',
  blue: '#1E88E5',
  orange: '#FF9800',
  gray: '#9E9E9E',
  white: '#FFFEFC',
} as const;

/** Texto principal usado em toda a plataforma. */
export const ink = '#323232';

/**
 * Derivados das cores da marca. As cores puras ficam para fundos, ícones e
 * bordas; texto pequeno usa a versão escura (`text`) para passar de 4.5:1
 * sobre o branco da marca. O cinza puro (#9E9E9E) dá só 2.6:1 sobre branco:
 * serve para borda, ícone e estado desabilitado, não para texto.
 */
const tone = (color: string) => ({
  solid: color,
  text: shade(0.42, color),
  soft: mix(0.12, color, brand.white),
  border: mix(0.35, color, brand.white),
});

export const tones = {
  green: tone(brand.green),
  blue: tone(brand.blue),
  orange: tone(brand.orange),
  gray: {
    ...tone(brand.gray),
    text: shade(0.35, brand.gray),
  },
  red: tone('#E53935'),
} as const;

export type TBrandTone = keyof typeof tones;

/** Ouro, prata e bronze do pódio — fora da paleta, só para posições 1 a 3. */
export const podium = {
  1: '#C99A06',
  2: '#8A96A3',
  3: '#B4682F',
} as const;

export const surface = {
  page: '#F6F7F9',
  card: brand.white,
  line: '#E2E2E2',
} as const;

/** Breakpoints da feature de torneio (rem). */
export const breakpoints = {
  sm: '30rem',
  md: '48rem',
  lg: '80rem',
} as const;

/** As mesmas faixas para `useMediaQuery`, para o JS e o CSS concordarem. */
export const media = {
  sm: `(max-width: ${breakpoints.sm})`,
  md: `(max-width: ${breakpoints.md})`,
  lg: `(max-width: ${breakpoints.lg})`,
} as const;

/** Alvo mínimo de toque: 44px mesmo quando o rem encolhe no celular. */
export const touchTarget = 'max(2.75rem, 44px)';

/**
 * Tamanho de fonte com piso. No celular a raiz do app cai para 12px e todo
 * rem encolhe junto (0.75rem vira 9px). O piso segura o texto no tamanho
 * que teria com raiz de 15px, nunca abaixo de 11px. Em telas largas, onde a
 * raiz já passa de 15px, vale o próprio rem.
 */
export const textSize = (rem: number) =>
  `max(${rem}rem, ${Math.max(11, Math.round(rem * 15 * 100) / 100)}px)`;

/**
 * Escala de títulos. Tracking e leading acompanham o tamanho: quanto maior o
 * texto, mais as letras parecem afastadas e as linhas soltas, então os dois
 * apertam. Texto corrido fica no padrão da fonte.
 */
export const displayType = css`
  line-height: 1.1;
  letter-spacing: -0.02em;
  text-wrap: balance;
`;

/** Título de seção ou card. */
export const titleType = css`
  line-height: 1.2;
  letter-spacing: -0.01em;
`;

/** Algarismos de largura fixa: horários, placares e cronômetros não tremem. */
export const tabularNums = css`
  font-variant-numeric: tabular-nums;
`;

/**
 * Base de interação de toda tela da feature de torneio, aplicada no
 * container de cada uma:
 * - toque sem atraso nem flash cinza, com resposta no instante do toque;
 * - anel de foco no teclado (o global zera `outline` em tudo);
 * - números tabulares em tabelas, horários e campos;
 * - campos com 16px no celular, abaixo disso o iOS dá zoom na página.
 */
export const touchFeedback = css`
  button,
  a,
  select {
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  button:not(:disabled):active {
    transform: scale(0.97);
  }

  button,
  a,
  select,
  summary,
  [role='button'],
  [role='tab'],
  [tabindex]:not([tabindex='-1']),
  input[type='checkbox'],
  input[type='radio'],
  input[type='range'] {
    &:focus-visible {
      outline: 2px solid ${brand.blue};
      outline-offset: 2px;
    }
  }

  table,
  time,
  input {
    ${tabularNums}
  }

  @media ${media.md} {
    input,
    select,
    textarea {
      /* !important: vários campos fixam a fonte em rem; o mínimo do iOS vence. */
      font-size: max(16px, 1em) !important;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    button:not(:disabled):active {
      transform: none;
    }
  }
`;
